import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Trophy } from "@phosphor-icons/react";
import { api, fileUrl } from "../../lib/api";
import { subscribeContentUpdates } from "../../lib/contentSync";
import { useLang } from "../../i18n";
import Chapter from "./Chapter";
import Reveal from "./Reveal";

const CATEGORIES = ["Speed", "Standart", "Slalom"];
const RANK_CLS = ["text-[#FFB800]", "text-racing-muted", "text-[#B0763F]"];

export default function KategoriPrestasi() {
    const { t } = useLang();
    const [category, setCategory] = useState("Speed");
    const [year, setYear] = useState(0);
    const [data, setData] = useState(null);

    useEffect(() => {
        let active = true;
        const load = () => {
            api.get("/scoreboard", { params: { category, year: year || undefined } })
                .then((r) => { if (active) setData(r.data); })
                .catch(() => { if (active) setData({ category, year, years: [], ranking: [] }); });
        };
        load();
        const unsubscribe = subscribeContentUpdates(load);
        return () => { active = false; unsubscribe(); };
    }, [category, year]);

    const years = data?.years || [];
    const selectedYear = data?.year || year;
    const ranking = data?.ranking || [];

    return (
        <section id="kategori-prestasi" className="py-24 md:py-32 border-t border-racing-line bg-[#FBF9F5]" data-testid="achievement-category-section">
            <div className="max-w-5xl mx-auto px-6 md:px-10">
                <Chapter number="03" title={t.ch_category_t} subtitle={t.ch_category_s} />
                <Reveal>
                    <div className="border border-racing-line bg-white" data-testid="home-category-scoreboard">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 border-b border-racing-line bg-racing-red px-5 py-4">
                            <div className="flex items-center gap-3">
                                <Trophy size={22} weight="duotone" className="text-racing-gold" />
                                <div>
                                    <h3 className="font-display uppercase text-2xl text-white" data-testid="home-scoreboard-title">Papan Skor {category}</h3>
                                    <p className="text-[10px] uppercase tracking-[0.22em] text-white/70">10 atlet teratas • klik nama untuk melihat prestasi</p>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {CATEGORIES.map((item) => (
                                    <button
                                        key={item}
                                        type="button"
                                        onClick={() => { setCategory(item); setYear(0); }}
                                        data-testid={`home-scoreboard-tab-${item.toLowerCase()}`}
                                        className={`px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] border transition-colors ${category === item ? "bg-white border-white text-racing-red" : "border-white/30 text-white/75 hover:text-white hover:border-white"}`}
                                    >
                                        {item}
                                    </button>
                                ))}
                                <select
                                    value={selectedYear}
                                    onChange={(e) => setYear(Number(e.target.value))}
                                    data-testid="home-scoreboard-year"
                                    className="bg-white border border-white/40 px-3 py-2.5 text-sm text-neutral-900 focus:outline-none"
                                >
                                    {years.map((item) => <option key={item} value={item}>{item}</option>)}
                                </select>
                            </div>
                        </div>

                        {!data ? (
                            <p className="p-6 text-sm text-racing-muted" data-testid="home-scoreboard-loading">Memuat papan skor...</p>
                        ) : ranking.length === 0 ? (
                            <p className="p-6 text-sm text-racing-muted" data-testid="home-scoreboard-empty">Belum ada medali {category} pada tahun {selectedYear}.</p>
                        ) : (
                            <div data-testid="home-scoreboard-list">
                                {ranking.map((entry, index) => (
                                    <div key={entry.name} className="grid grid-cols-12 gap-3 items-center px-5 py-3.5 border-b border-racing-line last:border-0 hover:bg-black/[0.02]" data-testid={`home-scoreboard-row-${index}`}>
                                        <span className={`col-span-1 font-display text-2xl ${RANK_CLS[index] || "text-black/25"}`}>{String(index + 1).padStart(2, "0")}</span>
                                        <span className="col-span-7 min-w-0 flex items-center gap-3">
                                            {entry.photo_url ? (
                                                <img src={fileUrl(entry.photo_url)} alt={entry.name} className="w-9 h-9 object-cover border border-racing-line shrink-0" data-testid={`home-scoreboard-photo-${index}`} />
                                            ) : (
                                                <span className="w-9 h-9 bg-racing-surface border border-racing-line flex items-center justify-center font-display text-racing-red shrink-0">{entry.name.charAt(0)}</span>
                                            )}
                                            {entry.athlete_id ? (
                                                <Link to={`/atlet/${entry.athlete_id}`} data-testid={`home-scoreboard-athlete-${index}`} className="font-medium uppercase text-sm text-neutral-900 hover:text-racing-red transition-colors truncate">
                                                    {entry.name}
                                                </Link>
                                            ) : (
                                                <span className="font-medium uppercase text-sm text-neutral-900 truncate">{entry.name}</span>
                                            )}
                                        </span>
                                        <span className="col-span-4 text-right text-[10px] uppercase tracking-[0.18em] text-racing-muted" data-testid={`home-scoreboard-medals-${index}`}>
                                            <span className="text-[#B8890B]">{entry.gold} Emas</span> • {entry.silver} Perak • <span className="text-[#B0763F]">{entry.bronze} Perunggu</span>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="border-t border-racing-line px-5 py-4 flex justify-end">
                            <Link
                                to={`/prestasi?category=${encodeURIComponent(category)}`}
                                data-testid="home-scoreboard-detail-link"
                                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-racing-red hover:text-racing-redhover transition-colors"
                            >
                                Lihat Detail Prestasi {category} <ArrowUpRight size={16} weight="bold" />
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
